import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, ViewChild } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatList, MatListItem, MatListItemLine, MatListItemMeta } from '@angular/material/list';
import { MatSelectChange, MatSelect, MatOption } from '@angular/material/select';
import { Router } from '@angular/router';
import { RootState } from '@next/core-lib/app';
import { AuthorizationSelectors } from '@next/core-lib/authorization';
import { ChipItem, PageDetailComponent, SummaryCardComponent } from '@next/core-lib/components';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { AgrUser, SelectedLocation, UserSelectors } from '@next/core-lib/user';
import { Store, select } from '@ngrx/store';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { filter, map, take, withLatestFrom } from 'rxjs/operators';
import { REGIONS } from 'src/app/shared/models/regions';

import {
  BodyConditionType,
  FeedAdvice,
  FeedAdviceProperties,
  FeedAdviceType,
  FeedIntakeTypes,
  FeedingType,
  GenderTypes,
  GeneticLineType,
  LocationItem,
  PhaseTransitionTypes,
} from '../../models/feed-advice.model';
import { SchemeSupportService } from '../../services/scheme-support.service';
import * as actions from '../../store/feed-advice.actions';
import {
  getFeedAdvicesFromReference,
  getSelectedFeedAdviceType,
} from '../../store/feed-advice.selectors';
import { FeedAdvicePropertiesComponent } from '../feed-advice-properties/feed-advice-properties.component';
import { SowsFeedAdvicePropertiesComponent } from '../sows/sows-feed-advice-properties/sows-feed-advice-properties.component';

@Component({
  selector: 'app-create-feed-advice-from-reference',
  templateUrl: './create-feed-advice-from-reference.component.html',
  styleUrls: ['./create-feed-advice-from-reference.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    PageDetailComponent,
    MatIcon,
    NgIf,
    FeedAdvicePropertiesComponent,
    SowsFeedAdvicePropertiesComponent,
    MatSelect,
    NgFor,
    MatOption,
    SummaryCardComponent,
    MatList,
    MatListItem,
    MatListItemLine,
    MatListItemMeta,
    MatButton,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class CreateFeedAdviceFromReferenceComponent implements OnDestroy {
  /* SELECTORS & DATA */
  location$: Observable<SelectedLocation> = this.store.select(UserSelectors.selectLocation);
  user$: Observable<AgrUser> = this.store.select(UserSelectors.selectUser);
  selectedRegionId: string;
  defaultSelectedCountryId: string;

  referenceFeedAdvices$: Observable<FeedAdvice[]> = this.store
    .select(getFeedAdvicesFromReference)
    .pipe(
      filter((referenceFeedAdvices): referenceFeedAdvices is FeedAdvice[] =>
        Array.isArray(referenceFeedAdvices),
      ),
    );
  filteredReferenceFeedAdvices$: BehaviorSubject<FeedAdvice[]> = new BehaviorSubject([]);
  subscriptions: Subscription[] = [];

  /* INDICATORS */
  isAdvisor = false;

  /* FILTERING PROPERTIES */
  feedintake: FeedIntakeTypes;
  phases: number;
  genders: ChipItem<number>[] = this.schemeSupportService.getSelectedGenders();
  feedAdviceType: FeedAdviceType;
  phaseTransition: PhaseTransitionTypes;
  bodyCondition: BodyConditionType;
  geneticLine: GeneticLineType;
  locations: LocationItem[];
  selectedLocations: number[] = [];

  locations$: Observable<LocationItem[]> = this.store.select(UserSelectors.selectLocations).pipe(
    map((userlocations) =>
      userlocations.map((userlocation) => ({
        value: userlocation.id,
        name: userlocation.name,
        selected: userlocation.isSelected,
      })),
    ),
  );

  regions: Array<{ id: string; value: string }> = REGIONS.sort((a, b) =>
    a.value < b.value ? -1 : 1,
  );

  @ViewChild(FeedAdvicePropertiesComponent) propertiesComponent: FeedAdvicePropertiesComponent;
  @ViewChild(SowsFeedAdvicePropertiesComponent)
  sowsPropertiesComponent: SowsFeedAdvicePropertiesComponent;

  constructor(
    private readonly store: Store<RootState>,
    private readonly router: Router,
    private readonly locale: LocaleService,
    private readonly schemeSupportService: SchemeSupportService,
  ) {
    this.subscriptions.push(
      this.store
        .pipe(select(AuthorizationSelectors.hasAtLeastAdvisorRole), take(1))
        .subscribe((hasAtLeastAdvisorRole) => {
          this.isAdvisor = hasAtLeastAdvisorRole;
        }),
    );

    this.locations$.subscribe((locations) => {
      this.locations = locations.filter((l) => l.selected);
      this.locations.forEach((l) => this.selectedLocations.push(l.value));
    });

    this.subscriptions.push(
      this.referenceFeedAdvices$.subscribe((referenceFeedAdvices) => {
        this.filteredReferenceFeedAdvices$.next(referenceFeedAdvices);
      }),
    );

    this.subscriptions.push(
      this.store
        .select(getSelectedFeedAdviceType)
        .pipe(withLatestFrom(this.location$), take(1))
        .subscribe(([feedAdviceType, location]) => {
          this.feedAdviceType = feedAdviceType;

          if (location) {
            this.defaultSelectedCountryId = location.country;
            this.updateRefFeedadvicesByCountry(location.country);
          }
        }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => {
      if (s) {
        s.unsubscribe();
      }
    });
  }

  getTitle(): string {
    const typeText = this.feedAdviceType
      ? `(${this.locale.translate('feedAdviceType.' + this.feedAdviceType.toLowerCase())})`
      : '';
    return `${this.locale.translate('from-reference.title')} ${typeText}`;
  }

  clearFilteringProperties() {
    if (this.isFatteningOrRearingOrPigletsFeedadvice()) {
      this.propertiesComponent.clearProperties();
    }
    if (this.isSowsFeedadvice()) {
      this.sowsPropertiesComponent.clearProperties();
    }
    this.updateRefFeedadvicesByCountry(this.defaultSelectedCountryId);
    this.changeFilteringProperties();
  }

  isFatteningOrRearingOrPigletsFeedadvice(): boolean {
    return (
      this.feedAdviceType === FeedAdviceType.FATTENING ||
      this.feedAdviceType === FeedAdviceType.REARING ||
      this.feedAdviceType === FeedAdviceType.WEANED_PIGLETS
    );
  }

  isSowsFeedadvice(): boolean {
    return (
      this.feedAdviceType === FeedAdviceType.SOWS_BREEDTOFARROW ||
      this.feedAdviceType === FeedAdviceType.SOWS_FARROWTOWEAN ||
      this.feedAdviceType === FeedAdviceType.SOWS_WEANTOBREED
    );
  }

  changeFilteringProperties(properties?: FeedAdviceProperties) {
    this.referenceFeedAdvices$.pipe(take(1)).subscribe((feedAdvices: FeedAdvice[]) => {
      if (!properties) {
        this.filteredReferenceFeedAdvices$.next(feedAdvices);
      } else {
        const filtered = feedAdvices.filter((advice) => {
          let result = true;

          if (properties.feedAdviceType) {
            result = result && advice.type === properties.feedAdviceType;
          }
          if (properties.bodyCondition) {
            result = result && advice.bodyCondition === properties.bodyCondition;
          }
          if (properties.geneticLine) {
            result = result && advice.geneticLine === properties.geneticLine;
          }
          if (properties.transitionDay) {
            result = result && advice.transitionDay === properties.transitionDay;
          }
          if (properties.weaningWeight) {
            result = result && advice.weaningWeight === properties.weaningWeight;
          }
          if (properties.weaningAge) {
            result = result && advice.weaningAge === properties.weaningAge;
          }
          if (properties.rearingAge) {
            result = result && advice.rearingAge === properties.rearingAge;
          }
          if (properties.pigletsPerFarrowingCategory) {
            result =
              result &&
              advice.pigletsPerFarrowingCategory === properties.pigletsPerFarrowingCategory;
          }
          if (properties.feedintake) {
            result = result && advice.feedIntake === properties.feedintake?.toString();
          }
          if (properties.phases) {
            result = result && advice.numberOfPhases === properties.phases;
          }
          if (properties.genders) {
            if (
              properties.genders.some(
                (g) => g === this.locale.translate(`gender.${GenderTypes.GILT}`).toLowerCase(),
              )
            ) {
              result = result && advice.isGilt;
            } else if (
              properties.genders.some(
                (g) => g === this.locale.translate(`gender.${GenderTypes.BOAR}`).toLowerCase(),
              )
            ) {
              result = result && advice.isBoar;
            } else if (
              properties.genders.some(
                (g) => g === this.locale.translate(`gender.${GenderTypes.BARROW}`).toLowerCase(),
              )
            ) {
              result = result && advice.isBarrow;
            }
          }
          if (properties.feedingType) {
            result =
              result &&
              (advice.feedingType === properties.feedingType ||
                (!advice.feedingType && properties.feedingType === FeedingType.CDI));
          }
          return result;
        });

        this.filteredReferenceFeedAdvices$.next(filtered);
      }
    });
  }

  changeRegionSelection(selection: MatSelectChange) {
    this.updateRefFeedadvicesByCountry(selection.value as string);
  }

  private updateRefFeedadvicesByCountry(country: string) {
    this.selectedRegionId = country;
    this.store.dispatch(actions.setSelectedCountry({ save: true, id: country as string }));
    this.store.dispatch(
      actions.navigateFromReferenceRequest({
        feedAdviceType: this.feedAdviceType,
      }),
    );
  }

  getReferenceSchemeInfo(referenceFeedAdvice: FeedAdvice): string {
    const genders = `${this.locale.translate(
      'from-reference.referencescheme-info.genders',
    )}: <strong>${this.schemeSupportService.stringifyGenders(
      referenceFeedAdvice.isBarrow,
      referenceFeedAdvice.isBoar,
      referenceFeedAdvice.isGilt,
    )}</strong>`;
    const feedIntake = referenceFeedAdvice.feedIntake
      ? `, ${this.locale.translate(
          'from-reference.referencescheme-info.feedintake',
        )}: <strong>${this.locale.translate(
          `feedintake.${referenceFeedAdvice.feedIntake.toLowerCase()}`,
        )}</strong>`
      : '';
    const nrOfPhases = `, ${this.locale.translate(
      'from-reference.referencescheme-info.phases',
    )}: <strong>${referenceFeedAdvice.numberOfPhases}</strong>`;
    const bodyCondition = referenceFeedAdvice.bodyCondition
      ? `${this.locale.translate(
          'from-reference.referencescheme-info.bodyCondition',
        )}: <strong>${this.locale.translate(
          `feed-advice-properties.body-condition.${referenceFeedAdvice.bodyCondition}`,
        )}</strong>`
      : '';
    const geneticLine = referenceFeedAdvice.geneticLine
      ? `, ${this.locale.translate('from-reference.referencescheme-info.geneticLine')}: <strong>${
          referenceFeedAdvice.geneticLine
        }</strong>`
      : '';
    const parity = referenceFeedAdvice.parity
      ? `, ${this.locale.translate(
          'from-reference.referencescheme-info.parity',
        )}: <strong>${this.locale.translate(
          `feed-advice-properties.parity-category.${referenceFeedAdvice.parity}`,
        )}</strong>`
      : '';
    const inputSystem = referenceFeedAdvice.inputSystem
      ? `, ${this.locale.translate(
          'from-reference.referencescheme-info.inputSystem',
        )}: <strong>${this.locale.translate(
          `feed-advice-properties.input-system.${referenceFeedAdvice.inputSystem}`,
        )}</strong>`
      : '';
    const pigletsPerFarrowingCategory = referenceFeedAdvice.pigletsPerFarrowingCategory
      ? `, ${this.locale.translate(
          'from-reference.referencescheme-info.pigletsPerFarrowingCategory',
        )}: <strong>${referenceFeedAdvice.pigletsPerFarrowingCategory}</strong>`
      : '';
    const transitionDay = referenceFeedAdvice.transitionDay
      ? `, ${this.locale.translate('from-reference.referencescheme-info.transitionDay')}: <strong>${
          referenceFeedAdvice.transitionDay
        }</strong>`
      : '';
    const weaningWeight = referenceFeedAdvice.weaningWeight
      ? `, ${this.locale.translate('from-reference.referencescheme-info.weaningWeight')}: <strong>${
          referenceFeedAdvice.weaningWeight
        }</strong>`
      : '';
    const weaningAge = referenceFeedAdvice.weaningAge
      ? `, ${this.locale.translate('from-reference.referencescheme-info.weaningAge')}: <strong>${
          referenceFeedAdvice.weaningAge
        }</strong>`
      : '';
    const rearingAge = referenceFeedAdvice.rearingAge
      ? `, ${this.locale.translate('from-reference.referencescheme-info.rearingAge')}: <strong>${
          referenceFeedAdvice.rearingAge
        }</strong>`
      : '';

    switch (this.feedAdviceType) {
      case FeedAdviceType.REARING:
        return `${genders}${feedIntake}${nrOfPhases}${rearingAge}`;
      case FeedAdviceType.FATTENING:
        return `${genders}${feedIntake}${nrOfPhases}`;
      case FeedAdviceType.WEANED_PIGLETS:
        return `${genders}${nrOfPhases}${weaningWeight}${weaningAge}`;
      case FeedAdviceType.SOWS_BREEDTOFARROW:
        return `${bodyCondition}${geneticLine}${parity}${inputSystem}${pigletsPerFarrowingCategory}${transitionDay}`;
      case FeedAdviceType.SOWS_WEANTOBREED:
      case FeedAdviceType.SOWS_FARROWTOWEAN:
        return `${geneticLine}${parity}${inputSystem}${pigletsPerFarrowingCategory}${transitionDay}`;
    }
  }

  back = () => this.router.navigate(['/feed-advice']);

  createFeedAdviceFromScratch() {
    if (this.isAdvisor) {
      this.store.dispatch(
        actions.navigateCreateFeedAdvice({
          isReferenceFeedAdvice: false,
          feedAdviceType: this.feedAdviceType,
        }),
      );
    }
  }

  createFeedAdviceFromReference(referenceFeedAdvice: FeedAdvice) {
    if (this.isAdvisor) {
      this.user$.pipe(take(1)).subscribe((user) => {
        this.store.dispatch(
          actions.setSelectedFeedAdviceFromReference({
            feedAdvice: <FeedAdvice>{
              ...referenceFeedAdvice,
              id: null,
              isReferenceFeedAdvice: false,
              usedReferenceFeedAdviceId: referenceFeedAdvice.id,
              advisorName: user.name,
              advisorProfileId: null,
              customerId: null,
              locationIds: this.selectedLocations,
            },
          }),
        );
        this.store.dispatch(
          actions.navigateCreateFeedAdvice({
            isReferenceFeedAdvice: false,
            feedAdviceType: this.feedAdviceType,
          }),
        );
      });
    }
  }
}
